import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewEncapsulation,
  ChangeDetectionStrategy
} from "@angular/core";
import { ActivatedRoute, ParamMap } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import FileSaver from "file-saver";
import { NGXLogger } from "ngx-logger";
import { BehaviorSubject, Subscription } from "rxjs";
import { ActionState } from "src/app/enums/action-state.enum";
import {
  CallLogColumns,
} from "src/app/enums/call-log/call-log-columns";
import { TableCallLogListMapper } from "src/app/mapper/table/table-call-log-list.mapper";
import { EventsActionFilters } from "src/app/models/events/eventsActionFilters";
import { EventColumn } from "src/app/models/events/eventsColumn";
import { CallLogRepo } from "src/app/repositories/call-log-repo";
import { AlertService } from "src/app/services";
import { IconType } from "src/app/tinea-components/icon/enum/icon-type";
import { LinkClickSateType } from "src/app/tinea-components/paging/enum/link-click-state-type";
import { PagingModel } from "src/app/tinea-components/paging/models/input/paging-model";
import { TableModel } from "src/app/tinea-components/table/models/input/table-model";
import { UiIcon } from "src/app/tinea-components/table/models/input/ui-icon";
import { TableOutputEvent } from "src/app/tinea-components/table/models/table-output-event";
import { TableOutputRowEvent } from "src/app/tinea-components/table/models/table-output-row-event";
import {
  ApplicationFeature,
  CallNotificationPagedList, CallNotificationSearchBody, CallNotificationSortFieldName, CallType,
  MatchingType,
} from 'src/swagger-gen/web-secured/models';
import { DisplayText } from "../../models/events/displayText";
import { AlertParameters, AlertType } from "../../models/models-index";
import { EventEmitterFilters } from "../../models/table/event-emitter-filters";
import FiltersConfig from "../../models/table/filters-config";

@Component({
    selector: "app-call-log-list",
    templateUrl: "./call-log-list.component.html",
    styleUrls: ["./call-log-list.component.scss"],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CallLogListComponent implements OnInit, OnDestroy {
  @Input() allowGlobalActions: boolean;
  @Input() filters: ParamMap;

  eUid: string;

  FEATURE = ApplicationFeature;

  table: TableModel;
  paging: PagingModel;

  callLogs: CallNotificationPagedList;
  filtersConfig: FiltersConfig;
  fullColumnList: Array<EventColumn>;

  matchingType = MatchingType.EXACT_MATCH_IGNORE_CASE;
  newItemFilter: EventsActionFilters = new EventsActionFilters();
  dataForChildComponent: any = {};

  loadingState = new BehaviorSubject(ActionState.LOADING);
  actionState = ActionState;

  private subscriptions: Subscription[] = [];
  errorMessage: string;
  acknowledgingAll = false;

  customFilterItemsList: EventsActionFilters[] = [];
  offsetEvent = 0;
  limitEvent = 25;

  isoldTableView: boolean;

  constructor(
    private logger: NGXLogger,
    private route: ActivatedRoute,
    private callLogRepo: CallLogRepo,
    private tableCallLogMapper: TableCallLogListMapper,
    private translate: TranslateService,
    private alertService: AlertService
  ) {
    this.eUid = this.route.snapshot.parent.params.uid;
    this.dataForChildComponent.body = {
      filters: [],
    };

    this.customFilterItemsList.push({ field: new DisplayText('TYPE', translate.instant(`call-log.type.${CallType.INTERPHONE}`)), value: CallType.INTERPHONE });
    this.customFilterItemsList.push({ field: new DisplayText('TYPE', translate.instant(`call-log.type.${CallType.TELEPHONE}`)), value: CallType.TELEPHONE });
    this.customFilterItemsList.push({ field: new DisplayText('TYPE', translate.instant(`call-log.type.${CallType.SMS}`)), value: CallType.SMS });
    this.customFilterItemsList.push({ field: new DisplayText('TYPE', translate.instant(`call-log.type.${CallType.OTHER}`)), value: CallType.OTHER });
    this.customFilterItemsList.push({ field: new DisplayText('TYPE', 'Tout'), value: '' });
  }

  ngOnInit() {
    this.fullColumnList = [
      new EventColumn(
        true,
        new DisplayText(
          CallLogColumns.CALL_DATE,
          this.translate.instant("call-log.table.date")
        )
      ),
      new EventColumn(
        true,
        new DisplayText(
          CallLogColumns.CALL_TYPE,
          this.translate.instant("call-log.table.type")
        )
      ),
      new EventColumn(
        true,
        new DisplayText(
          CallLogColumns.INTERVENANT_CODE,
          this.translate.instant("call-log.table.intervenantCode")
        )
      ),
      new EventColumn(
        true,
        new DisplayText(
          CallLogColumns.DESCRIPTION,
          this.translate.instant("call-log.table.description")
        )
      ),
      new EventColumn(
        true,
        new DisplayText(
          CallLogColumns.PANNE,
          this.translate.instant("call-log.table.panne")
        )
      ),
      new EventColumn(
        true,
        new DisplayText(
          CallLogColumns.CALL_STATUS,
          this.translate.instant("call-log.table.status")
        )
      ),
      new EventColumn(
        true,
        new DisplayText(
          CallLogColumns.CREATE_TICKET,
          this.translate.instant("call-log.table.createTicket")
        )
      ),
      // new EventColumn(true, new DisplayText(CallLogColumns.REGROUPEMENT, this.translate.instant('call-log.table.regroupement'))),
    ];
    this.fullColumnList.push(
      new EventColumn(
        true,
        new DisplayText(
          CallLogColumns.STATION_CODE,
          this.translate.instant("call-log.table.code")
        )
      )
    );
    this.fullColumnList.push(
      new EventColumn(
        true,
        new DisplayText(
          CallLogColumns.STATION,
          this.translate.instant("call-log.table.station")
        )
      )
    );
    this.filtersConfig = {
      date: true,
      export: {
        show: true,
        isLoading: false,
      },
      column: {
        fullColumnList: this.fullColumnList,
      },
      customFilter: {
        paramsToInclude: this.customFilterItemsList,
      },
      sort: {
        paramsToInclude: Object.values(CallNotificationSortFieldName),
        defaultSort: CallNotificationSortFieldName.CALL_DATE,
      },
      search: {
        paramsToInclude: ['STATION_CODE', 'STATION', 'DESCRIPTION', 'PANNE'],
      },
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.filters && changes.filters.currentValue) {
      // TODO
      // this.dataForChildComponent.body.filters = this.getFiltersFromParams(this.filters);
    }
  }

  /***************************************  APPEL API  **********************************/
  getCallLogs() {
    const params = this.dataForChildComponent.params || [];
    const filterBody = this.getBody();
    const body: CallNotificationSearchBody = {};

    this.loadingState.next(ActionState.LOADING);
    this.isoldTableView = false;

    const searchFilter = filterBody.filters.find(f => f.field === 'ALL')?.value;
    const searchParam = params.find(p => p.key === 'search');
    if (searchParam) {
      searchParam.value = searchFilter ?? '';
    } else {
      params.push({key: 'search', value: searchFilter ?? ''});
    }

    const stationCodeFilter = filterBody.filters.find(f => f.field === 'STATION_CODE')?.value;
    if (stationCodeFilter) {
      body.stationCode = stationCodeFilter;
    }

    const stationFilter = filterBody.filters.find(f => f.field === 'STATION')?.value;
    if (stationFilter) {
      body.stationName = stationFilter;
    }

    const descriptionFilter = filterBody.filters.find(f => f.field === 'DESCRIPTION')?.value;
    if (descriptionFilter) {
      body.description = descriptionFilter;
    }

    const panneFilter = filterBody.filters.find(f => f.field === 'PANNE')?.value;
    if (panneFilter) {
      body.panne = panneFilter;
    }

    const typeFilter = filterBody.filters.find(f => f.field === 'TYPE')?.value;
    if (typeFilter) {
      body.callType = [typeFilter];
    }

    this.callLogRepo
      .getCallLogs(params, body, this.limitEvent, this.offsetEvent)
      .toPromise()
      .then(
        (logs: CallNotificationPagedList) => {
          this.handleGetCallLogResponse(logs);
        },
        (error) => {
          this.handleGetCallLogErrors(error);
        }
      );
  }

  getBody() {
    return this.dataForChildComponent.body;
  }

  handleGetCallLogResponse(callLogs: CallNotificationPagedList) {
    this.callLogs = callLogs;
    this.loadingState.next(ActionState.READY);
    this.createTableMapping();
    this.createPagingMapping();
  }

  handleGetCallLogErrors(error) {
    this.loadingState.next(ActionState.READY);
    this.logger.log(error);
    const alertParams = new AlertParameters(
      false,
      false,
      true,
      false,
      false,
      false,
      true,
      this.translate.instant("error.title")
    );
    this.alertService.showAlert(error, AlertType.ERROR, alertParams);
  }

  onNewAlarmList() {
    this.createTableMapping();
  }

  /***************************************  PAGING MAPPING  **********************************/
  createPagingMapping() {
    this.paging = this.generatePaging();
  }

  generatePaging(): PagingModel {
    const paging = new PagingModel(
      this.callLogs.paging.offset,
      this.limitEvent,
      this.callLogs.paging.curItems,
      this.callLogs.paging.totalItems,
      this.callLogs.paging.nextItemsLink,
      this.callLogs.paging.previousItemsLink
    );

    // configuration
    paging.configuration.actionIfNoLink = "disabled";
    paging.configuration.pagingEnabled = false;
    paging.configuration.rangeEnabled = true;
    // prevLink
    const prevLinkElement = new UiIcon();
    prevLinkElement.type = IconType.FONTAWESOME;
    prevLinkElement.value = "icon-arrow-left-circle";
    paging.configuration.prevLinkElement = prevLinkElement;
    // nextLink
    const nextLinkElement = new UiIcon();
    nextLinkElement.type = IconType.FONTAWESOME;
    nextLinkElement.value = "icon-arrow-right-circle";
    paging.configuration.nextLinkElement = nextLinkElement;

    return paging;
  }

  eventOnClickPrevLink(event: LinkClickSateType): void {
    if (event === LinkClickSateType.ALLOWED) {
      this.onPagination("prev-event");
    }
  }

  eventOnClickNextLink(event: LinkClickSateType): void {
    if (event === LinkClickSateType.ALLOWED) {
      this.onPagination("next-event");
    }
  }

  // TODO: refacto to Paging Component
  onPagination(type: "next-event" | "prev-event") {
    if (type === "next-event") {
      this.onNextPaginationEvent();
    } else if (type === "prev-event") {
      this.onPrevPaginationEvent();
    }
  }

  onPrevPaginationEvent() {
    this.offsetEvent = this.offsetEvent - this.limitEvent;
    this.getCallLogs();
  }

  onNextPaginationEvent() {
    this.offsetEvent = this.offsetEvent + this.limitEvent;
    this.getCallLogs();
  }

  /***************************************  TABLE MAPPING  **********************************/
  createTableMapping() {
    const table: TableModel = this.tableCallLogMapper.init(this.callLogs);
    this.table = table;
  }

  onMouseenterRowEventOfTable(event: TableOutputRowEvent) {
    if (event.isDataRowTarget()) {
    }
  }

  onMouseleaveRowEventOfTable(event: TableOutputRowEvent) {
    if (event.isDataRowTarget()) {
    }
  }

  onEventOnClick(event: TableOutputEvent) {}

  /***************************************  FILTERS EVENT  **********************************/
  eventReceiver(event: any) {
    const typeEvent = event.type;
    const dataEvent = event.data;
    switch (typeEvent) {
      case EventEmitterFilters.PARAMS:
        this.getParamsForGetEvents(dataEvent);
        break;
      case EventEmitterFilters.BODY:
        this.getBodyForGetEvents(dataEvent);
        break;
      case EventEmitterFilters.SHOW_HIDE_COLUMN:
        this.addRemoveColumnItem(dataEvent);
        break;
      case EventEmitterFilters.EXPORT:
        this.exportData();
        break;
      default:
        return;
    }
  }

  exportData() {
    this.filtersConfig.export.isLoading = true;
    let params = [];
    params = this.dataForChildComponent.params || [];

    this.callLogRepo
      .exportCallLog(params)
      .toPromise()
      .then((response) => {
        // this.loggerService.trace ('in StationTicketsTableComponent getTickets(): got tickets');
        const filename = response.headers.get("content-disposition").split("=");
        const blob = new Blob([response.body], {
          type: response.headers.get("content-type"),
        });
        FileSaver.saveAs(blob, filename[1].slice(1, -1));
        this.filtersConfig.export.isLoading = false;
      })
      .catch((error) => {
        this.filtersConfig.export.isLoading = false;
        const title = this.translate.instant("error.title");
        const alertParams = new AlertParameters(
          false,
          false,
          true,
          false,
          false,
          false,
          true,
          title
        );
        this.alertService.showAlert(error, AlertType.ERROR, alertParams);
      });
  }

  getParamsForGetEvents(event: any): void {
    // this.loggerService.log('in getParamsForGetEvents');
    this.dataForChildComponent.params = event;

    if (this.dataForChildComponent.params.length > 0) this.getCallLogs();
  }

  getBodyForGetEvents(event: any): void {
    // this.loggerService.log('in getBodyForGetEvents');
    this.dataForChildComponent.body = event;

    if (this.dataForChildComponent.body.filters.length > 0) this.getCallLogs();
  }

  addRemoveColumnItem(event: any): void {
    const columnKey = event.columnKey;
    const show = event.show;
    this.table.dataRow.forEach((r) => {
      r.cells.forEach((c) => {
        this.showOrHideCell(c, columnKey, show);
      });
    });
    this.addRemoveColumnToTableHeader(columnKey, show);
  }

  addRemoveColumnToTableHeader(columnKey: string, show: boolean) {
    this.table.header.cells.forEach((c) => {
      this.showOrHideCell(c, columnKey, show);
    });
  }

  showOrHideCell(c: any, columnKey: string, show: boolean) {
    const types = CallLogColumns.values();
    types.forEach((type) => {
      if (columnKey === type && c.columnId === type) {
        c.show = show;
      }
    });
  }

  ngOnDestroy() {
    this.callLogs = null;
    if (this.subscriptions !== undefined) {
      this.subscriptions.forEach((s) => s.unsubscribe());
    }
  }
}
